import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { Suspense, lazy } from 'react';
import {
  TooltipProvider,
  AppErrorBoundary,
  PageLoader,
  NotFoundPage,
  UnauthorizedPage,
  NotificationToastBridge,
  campaignSearchSchema,
  campaignsListSearchSchema,
  shopifyDiscountsSearchSchema,
} from '@discovr/ui';

// Auth pages — small, no lazy load needed
import LoginPage from '@/pages/LoginPage';
import SharedLinkGatePage from '@/pages/SharedLinkGatePage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import OtpLoginPage from '@/pages/OtpLoginPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import EmailActionHandlerPage from '@/pages/EmailActionHandlerPage';
import WaitlistVerifyPage from '@/pages/WaitlistVerifyPage';
import WaitlistPage from '@/pages/WaitlistPage';
import InviteAcceptPage from '@/pages/InviteAcceptPage';
import ActivatePage from '@/pages/ActivatePage';
import JoinLookupPage from '@/pages/JoinLookupPage';
import JoinPage from '@/pages/JoinPage';
import JoinAcceptPage from '@/pages/JoinAcceptPage';

// Typed search schemas for the public auth pages (zod v4 → Standard Schema).
import {
  loginSearchSchema,
  resetSearchSchema,
  inviteSearchSchema,
  authActionSearchSchema,
  waitlistVerifySearchSchema,
  otpLoginSearchSchema,
  joinSearchSchema,
  viewSearchSchema,
} from '@/auth/search';
import { newCampaignSearchSchema } from '@/dashboard/NewCampaign/search';

// Layout — eager (it owns the auth guard + sidebar). Renders <Outlet/> for its children.
import BrandLayout from '@/layouts/BrandLayout';

// Dashboard tabs (lazy — rendered inside BrandLayout via <Outlet>)
const BrandOverviewTab = lazy(() =>
  import('@/dashboard/brand-tabs').then((m) => ({ default: m.BrandOverviewTab })),
);
const BrandCampaignsTab = lazy(() =>
  import('@/dashboard/brand-tabs').then((m) => ({ default: m.BrandCampaignsTab })),
);
const BrandHelpTab = lazy(() =>
  import('@/dashboard/brand-tabs').then((m) => ({ default: m.BrandHelpTab })),
);
const ShopifyDiscountsPage = lazy(() =>
  import('@/dashboard/brand-tabs').then((m) => ({ default: m.ShopifyDiscountsPage })),
);
const BrandIntegrationsTab = lazy(() =>
  import('@/dashboard/brand-tabs').then((m) => ({ default: m.BrandIntegrationsTab })),
);
const NewCampaign = lazy(() =>
  import('@/dashboard/NewCampaign').then((m) => ({ default: m.NewCampaign })),
);
const CampaignDetails = lazy(() =>
  import('@/dashboard/campaign-detail/CampaignDetailsPage').then((m) => ({
    default: m.CampaignDetailsPage,
  })),
);
const ShortLinkResolverPage = lazy(() => import('@/pages/ShortLinkResolverPage'));

// Shared pages
const Profile = lazy(() => import('@/shared/Profile').then((m) => ({ default: m.Profile })));
const SettingsPage = lazy(() =>
  import('@/shared/SettingsPage').then((m) => ({ default: m.BrandSettingsPage })),
);

// Legal pages from @discovr/ui
const PrivacyPolicy = lazy(() =>
  import('@discovr/ui').then((m) => ({ default: m.PrivacyPolicy })),
);
const TermsOfService = lazy(() =>
  import('@discovr/ui').then((m) => ({ default: m.TermsOfService })),
);

// Auth state threaded in from main.tsx so beforeLoad can guard synchronously.
// The router mount is gated on `loading`, so `isAuthenticated` is final by the
// time beforeLoad runs (no false redirect).
interface RouterAuthContext {
  isAuthenticated: boolean;
}

const rootRoute = createRootRouteWithContext<{ auth: RouterAuthContext }>()({
  component: () => (
    <TooltipProvider>
      <AppErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <NotificationToastBridge />
          <Outlet />
        </Suspense>
        {import.meta.env.DEV && <TanStackRouterDevtools />}
      </AppErrorBoundary>
    </TooltipProvider>
  ),
  notFoundComponent: () => <NotFoundPage />,
});

// ── Public routes ──────────────────────────────────────────────────────────
// Typed search via `validateSearch` (zod v4 schema passed DIRECTLY — Standard
// Schema, no zodValidator). Pages read the typed values with Route.useSearch().
const loginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login', component: LoginPage, validateSearch: loginSearchSchema });
// Public sign-in gate for a shared campaign link opened while signed out.
const viewRoute = createRoute({ getParentRoute: () => rootRoute, path: '/view', component: SharedLinkGatePage, validateSearch: viewSearchSchema });
const otpLoginRoute = createRoute({ getParentRoute: () => rootRoute, path: '/login/code', component: OtpLoginPage, validateSearch: otpLoginSearchSchema });
const forgotPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/forgot-password', component: ForgotPasswordPage });
const resetPasswordRoute = createRoute({ getParentRoute: () => rootRoute, path: '/reset-password', component: ResetPasswordPage, validateSearch: resetSearchSchema });
const authActionRoute = createRoute({ getParentRoute: () => rootRoute, path: '/auth/action', component: EmailActionHandlerPage, validateSearch: authActionSearchSchema });
const waitlistVerifyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/waitlist/verify', component: WaitlistVerifyPage, validateSearch: waitlistVerifySearchSchema });
const waitlistRoute = createRoute({ getParentRoute: () => rootRoute, path: '/waitlist', component: WaitlistPage });
const inviteRoute = createRoute({ getParentRoute: () => rootRoute, path: '/invite', component: InviteAcceptPage, validateSearch: inviteSearchSchema });
const activateRoute = createRoute({ getParentRoute: () => rootRoute, path: '/activate', component: ActivatePage, validateSearch: inviteSearchSchema });
const joinLookupRoute = createRoute({ getParentRoute: () => rootRoute, path: '/join', component: JoinLookupPage, validateSearch: joinSearchSchema });
const joinRoute = createRoute({ getParentRoute: () => rootRoute, path: '/join/$brandSlug', component: JoinPage, validateSearch: joinSearchSchema });
const joinAcceptRoute = createRoute({ getParentRoute: () => rootRoute, path: '/join/accept', component: JoinAcceptPage, validateSearch: inviteSearchSchema });
const privacyPolicyRoute = createRoute({ getParentRoute: () => rootRoute, path: '/privacy-policy', component: PrivacyPolicy });
const termsRoute = createRoute({ getParentRoute: () => rootRoute, path: '/terms', component: TermsOfService });
const unauthorizedRoute = createRoute({ getParentRoute: () => rootRoute, path: '/unauthorized', component: UnauthorizedPage });

// ── Protected pathless layout route = BrandLayout ──
// beforeLoad owns the initial auth guard, redirecting unauthenticated users to
// /login with the attempted URL so they return here after signing in.
const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'protected',
  component: BrandLayout,
  beforeLoad: ({ context, location }) => {
    if (!context.auth.isAuthenticated) {
      // A shared campaign link opened while signed out lands on the "Sign in to
      // view this" gate; every other route goes to login. Both forward the URL.
      const to = location.pathname.startsWith('/campaign/') ? '/view' : '/login';
      throw redirect({ to, search: { redirect: location.href } });
    }
  },
});

const indexRoute = createRoute({ getParentRoute: () => protectedRoute, path: '/', component: BrandOverviewTab });
const campaignsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'campaigns',
  component: BrandCampaignsTab,
  validateSearch: campaignsListSearchSchema,
});
const newCampaignRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'new-campaign',
  component: NewCampaign,
  validateSearch: newCampaignSearchSchema,
});
const helpRoute = createRoute({ getParentRoute: () => protectedRoute, path: 'help', component: BrandHelpTab });
const integrationsRoute = createRoute({ getParentRoute: () => protectedRoute, path: 'integrations', component: BrandIntegrationsTab });
const shopifyDiscountsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'integrations/shopify/discounts',
  component: ShopifyDiscountsPage,
  validateSearch: shopifyDiscountsSearchSchema,
});
const settingsRoute = createRoute({ getParentRoute: () => protectedRoute, path: 'settings', component: SettingsPage });
const profileRoute = createRoute({ getParentRoute: () => protectedRoute, path: 'profile', component: Profile });

const campaignDetailRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: 'campaign/$campaignParam',
  component: CampaignDetails,
  validateSearch: campaignSearchSchema, // zod v4 schema passed DIRECTLY (Standard Schema). No zodValidator.
});

// Short shareable links — /s/<campCode>[/<view path>] → canonical URL. Public
// (parented off rootRoute) so the resolver can branch on auth state and let an
// anonymous visitor reach the view-only share page instead of being bounced.
const shortLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 's/$campCode',
  component: ShortLinkResolverPage,
});
const shortLinkTailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 's/$campCode/$',
  component: ShortLinkResolverPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  viewRoute,
  otpLoginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  authActionRoute,
  waitlistVerifyRoute,
  waitlistRoute,
  inviteRoute,
  activateRoute,
  joinLookupRoute,
  joinRoute,
  joinAcceptRoute,
  privacyPolicyRoute,
  termsRoute,
  unauthorizedRoute,
  shortLinkRoute,
  shortLinkTailRoute,
  protectedRoute.addChildren([
    indexRoute,
    campaignsRoute,
    newCampaignRoute,
    helpRoute,
    integrationsRoute,
    shopifyDiscountsRoute,
    settingsRoute,
    profileRoute,
    campaignDetailRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // Real auth context is supplied per-render by <InnerApp> in main.tsx; this is
  // just the default required by createRootRouteWithContext.
  context: { auth: { isAuthenticated: false } },
  // Scroll handling runs only when the (pathname, ?tab) pair changes — a real
  // screen change. Within a screen, param navigations (?pill/?page/?size/
  // ?search) leave scroll untouched, so changing a roster filter doesn't reset
  // to top or replay a stale cached position.
  scrollRestoration: (() => {
    let lastScreenKey: string | undefined;
    return ({ location }: { location: { pathname: string; search: unknown } }) => {
      const tab = (location.search as { tab?: string } | undefined)?.tab ?? '';
      const screenKey = `${location.pathname}#${tab}`;
      const screenChanged = screenKey !== lastScreenKey;
      lastScreenKey = screenKey;
      return screenChanged;
    };
  })(),
  defaultPreload: 'intent',
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
